import { poll } from 'api/shared';
import { formatAdvancedRequestBody, formatManualRequestBody } from './templateCallRequestBodyFormatters';
import networkAdapter from '../adapters/network';
import { camelCaseKeys } from 'util/mapKeys';
import { gql } from 'graphql-request';
import { uapiFetcher } from 'util/UAPI';
import { trackError } from 'util/error';

export const _sendBeginDownloadScripts = ({ url, requestParams={}, userId }) => {
  return new Promise((resolve, reject) => {
    const legacyOptions = {
      url: url,
      userId,
      method: 'POST',
      body: JSON.stringify(requestParams)
    };
    
    networkAdapter({
      legacyOptions
    })
    .then((response) => {
      resolve(response.jobUUID);
    })
    .catch((err) => {
      reject(err);
    });
  });
};


/**
 * Mycroft only supports "si" and "imperial" as unit regimes, but the front-end supports more
 * than that (e.g. "canada"). This function will return either "si" or "imperial" as a best match
 * for a front-end unit regime.
 * @param {String} unitTemplate
 * @returns {String}
 */
export const getUnitTemplateForMycroft = unitTemplate => {
  if (unitTemplate === 'imperial' || unitTemplate === 'canada') {
    return 'imperial';
  }
  return 'si';
};

export const sendBeginSingleDownloadScript = ({ 
  fieldId, 
  preferredDeviceId, 
  scriptId, 
  unitTemplate, 
  userId, 
  rowSpacing }) => {
  const mycroftUnits = getUnitTemplateForMycroft(unitTemplate);
  const rowSpacingVal = rowSpacing ? '&row-spacing=' + rowSpacing.toString() : '';

  return _sendBeginDownloadScripts({
    url: `/api/mycroft/v2/prescriptions/download/${fieldId}/${scriptId}`
      + `?format=${preferredDeviceId}&units=${mycroftUnits}${rowSpacingVal}`,
    userId
  });
};

export const sendBeginBulkDownloadScripts = ({
  fieldIds, 
  preferredDeviceId, 
  primaryOnly, 
  scriptType, 
  season, 
  unitTemplate, 
  userId, 
  rowSpacing }) => {
  const mycroftUnits = getUnitTemplateForMycroft(unitTemplate);
  const rowSpacingVal = rowSpacing ? '&row-spacing=' + rowSpacing.toString() : '';
  
  const defaultParams = {
    'activity-prefix': scriptType,
    'field-ids': fieldIds,
    'monitor-format': preferredDeviceId,
    'season': season
  };
  const requestParams = defaultParams;

  return _sendBeginDownloadScripts({
    url: `/api/mycroft/v2/prescriptions/download/bulk?primary-only=${primaryOnly}`
    + `&units=${mycroftUnits}${rowSpacingVal}`,
    requestParams,
    userId
  });
};

export const sendDeleteScriptMutation = ({ scriptId }) => {
  const mutation = gql`
  mutation DeletePrescription($id: ID!) {
    deletePrescription(id: $id) {
      id
      itemStatus
    }
  }
`;

  return uapiFetcher(mutation, { id: scriptId });
};


export const sendFetchScriptList = ( {userId, fieldUuids, primaryScriptsOnly } ) => {
  if( !fieldUuids ) {
    // this in particular triggers a misleading 401, so avoid that
    return Promise.reject('No fieldId sent to groot for script list');
  }

  const legacyOptions = {
    url: `/api/groot/v2/prescriptions/summary${ primaryScriptsOnly ? '?primary-only=true' : '' }`,
    userId,
    method: 'POST',
    redirectsToLoginOnAuthFail: false,
    body: JSON.stringify({'field-ids': fieldUuids })
  };
  
  return networkAdapter({
    legacyOptions
  });
};

export const sendSaveScriptTankMix = ({ fieldId, script, userId }) => {
  const updatedScript = {
    ...script,
    fieldId,
    prescriptionId: script.id
  };
  const camelCaseScript = camelCaseKeys(updatedScript);
  camelCaseScript.geometry = JSON.stringify(camelCaseScript.geometry);


  const graphRequest = {
    userId,
    query: `
      mutation updatePrescriptionDetails($input:UpdatePrescriptionInput!) {
        updatePrescriptionDetails(input:$input) {
          message
          oldFmzVersion
          newFmzVersion
          id
        }
      }
    `,
    variables: {
      input: camelCaseScript
    },
    olVersion: '2'
  };
  return networkAdapter({
    options: graphRequest,
    useOL: true
  });
};

export const sendUpdatePrescriptionAsPrimary = ({ fieldUuid, scriptId }) => {
  const query = gql`
    mutation UpdatePrescriptionAsPrimary($input: UpdatePrescriptionAsPrimaryInput!) {
      updatePrescriptionAsPrimary(input: $input) {
        id
        primary
      }
    }
  `;

  const variables = {
    input: {
      fieldId: fieldUuid,
      id: scriptId
    }
  };

  try {
    return uapiFetcher(query, variables);
  } catch (error) {
    console.log('Error in sendUpdatePrescriptionAsPrimary:', error);
    trackError(new TypeError(
      'Could not fetch "UpdatePrescriptionAsPrimary" mutation from UAPI'), { error, query, variables }
    );
    throw error;
  }
};

export const sendUpdateScriptMetadata = ({ fieldId, updatedMetadata, scriptId, userId }) => {
  const legacyOptions = {
    url: `/api/groot/v2/prescriptions/${fieldId}/${scriptId}/metadata`,
    userId,
    method: 'PATCH',
    redirectsToLoginOnAuthFail: false,
    body: JSON.stringify(updatedMetadata)
  };
  
  return networkAdapter({
    legacyOptions
  });
};


export const _sendFetchScriptTemplate = ( {activityUuid, userId, fieldId, requestParams} ) => {
  const queryString = activityUuid ? `?activity-uuid=${activityUuid}` : '';

  return poll(() => {
    const legacyOptions = {
      url: `/api/groot/v2/prescriptions/template/${fieldId}${queryString}`,
      userId,
      method: 'POST',
      redirectsToLoginOnAuthFail: false,
      body: JSON.stringify(requestParams)
    };
  
    return networkAdapter({
      legacyOptions
    });
  });
};

export const sendFetchAdvancedScriptTemplate = ( { cropId, cropNominalWeight, fieldId, templateParams, userId} ) => {
  return Promise.resolve()
  .then( () => {
    const requestParams = formatAdvancedRequestBody({ cropId, cropNominalWeight, templateParams });
    return _sendFetchScriptTemplate({ userId, fieldId, requestParams });
  });
};

export const sendFetchManualScriptTemplate = ( {activityUuid, cropId, fieldId, templateParams, userId} ) => {
  return Promise.resolve()
  .then( () => {
    const requestParams = formatManualRequestBody({ cropId, templateParams });
    return _sendFetchScriptTemplate({ activityUuid, fieldId, requestParams, userId });
  });
};

export const unitTestPrivate = {
  _sendBeginDownloadScripts,
  _sendFetchScriptTemplate
};
